/**
 * Error Handling Utilities
 * Centralized error logging, classification, and user-friendly messages
 */

// ============= ERROR TYPES =============

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorCategory = 'network' | 'api' | 'auth' | 'validation' | 'parse' | 'unknown';

export interface AppError {
  message: string;
  code?: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  userMessage: string;
  context?: Record<string, unknown>;
  originalError?: Error;
  timestamp: Date;
}

// ============= ERROR CLASSIFICATION =============

/**
 * Classifies an error and returns structured error info
 */
export function classifyError(error: unknown, context?: Record<string, unknown>): AppError {
  const timestamp = new Date();
  const originalError = error instanceof Error ? error : new Error(String(error));
  const message = originalError.message || 'Unknown error occurred';

  // Network errors
  if (isNetworkError(error)) {
    return {
      message,
      category: 'network',
      severity: 'medium',
      userMessage: 'Unable to connect. Please check your internet connection and try again.',
      context,
      originalError,
      timestamp,
    };
  }

  // API rate limit errors
  if (isRateLimitError(error)) {
    return {
      message,
      code: '429',
      category: 'api',
      severity: 'medium',
      userMessage: 'Too many requests. Please wait a moment and try again.',
      context,
      originalError,
      timestamp,
    };
  }

  // API server errors (5xx)
  if (isServerError(error)) {
    return {
      message,
      code: '5xx',
      category: 'api',
      severity: 'high',
      userMessage: 'Our servers are experiencing issues. Please try again in a few moments.',
      context,
      originalError,
      timestamp,
    };
  }

  // Authentication errors
  if (isAuthError(error)) {
    return {
      message,
      category: 'auth',
      severity: 'low',
      userMessage: 'Please sign in to continue.',
      context,
      originalError,
      timestamp,
    };
  }

  // Parse/validation errors
  if (isParseError(error)) {
    return {
      message,
      category: 'parse',
      severity: 'medium',
      userMessage: 'We received unexpected data. Please try again.',
      context,
      originalError,
      timestamp,
    };
  }

  // Default unknown error
  return {
    message,
    category: 'unknown',
    severity: 'medium',
    userMessage: "Something went wrong. Please try again.",
    context,
    originalError,
    timestamp,
  };
}

// ============= ERROR DETECTION HELPERS =============

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('network') ||
      msg.includes('failed to fetch') ||
      msg.includes('net::err') ||
      msg.includes('networkerror') ||
      msg.includes('connection') ||
      msg.includes('offline')
    );
  }
  return false;
}

function isRateLimitError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('429') ||
      msg.includes('rate limit') ||
      msg.includes('too many requests') ||
      msg.includes('quota exceeded')
    );
  }
  return false;
}

function isServerError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('500') ||
      msg.includes('502') ||
      msg.includes('503') ||
      msg.includes('504') ||
      msg.includes('internal server error') ||
      msg.includes('bad gateway') ||
      msg.includes('service unavailable')
    );
  }
  return false;
}

function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('401') ||
      msg.includes('403') ||
      msg.includes('unauthorized') ||
      msg.includes('not authenticated') ||
      msg.includes('jwt') ||
      msg.includes('token')
    );
  }
  return false;
}

function isParseError(error: unknown): boolean {
  if (error instanceof SyntaxError) {
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('json') ||
      msg.includes('parse') ||
      msg.includes('unexpected token') ||
      msg.includes('invalid')
    );
  }
  return false;
}

// ============= ERROR LOGGING =============

/**
 * Logs an error with full context for debugging
 */
export function logError(
  source: string,
  error: unknown,
  context?: Record<string, unknown>
): AppError {
  const appError = classifyError(error, context);
  
  // Format log message with context
  const logData = {
    source,
    timestamp: appError.timestamp.toISOString(),
    category: appError.category,
    severity: appError.severity,
    message: appError.message,
    code: appError.code,
    context: appError.context,
    stack: appError.originalError?.stack,
  };

  // Log based on severity - production logs are sanitized
  // Never log sensitive data like tokens, passwords, or session info
  const sanitizedLogData = {
    message: logData.message,
    code: logData.code,
    // Omit context and stack in production to prevent info leakage
    ...(import.meta.env.DEV ? { context: logData.context } : {}),
  };
  
  switch (appError.severity) {
    case 'critical':
    case 'high':
      console.error(`[${source}] ERROR:`, sanitizedLogData);
      break;
    case 'medium':
      console.warn(`[${source}] WARN:`, sanitizedLogData);
      break;
    default:
      // Info logs only in development
      if (import.meta.env.DEV) {
        console.log(`[${source}] INFO:`, sanitizedLogData);
      }
  }

  return appError;
}

// ============= USER-FRIENDLY MESSAGES =============

/**
 * Gets a user-friendly error message for display
 */
export function getUserMessage(error: unknown): string {
  const appError = classifyError(error);
  return appError.userMessage;
}

/**
 * Gets a specific message for data loading failures
 */
export function getDataLoadMessage(dataType: string): string {
  return `We're having trouble loading ${dataType} right now. Please try again in a few moments.`;
}

/**
 * Gets a message for partial data load (e.g., matches loaded but odds failed)
 */
export function getPartialLoadMessage(loaded: string, failed: string): string {
  return `${loaded} loaded successfully, but ${failed} is temporarily unavailable.`;
}

// ============= RETRY UTILITIES =============

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: unknown) => boolean;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  shouldRetry: (error) => {
    const appError = classifyError(error);
    // Only retry network and server errors, not auth or validation errors
    return appError.category === 'network' || appError.category === 'api';
  },
};

/**
 * Executes an async function with retry logic
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  source: string,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Log the error
      logError(source, error, { attempt, maxRetries: opts.maxRetries });

      // Check if we should retry
      if (attempt < opts.maxRetries && opts.shouldRetry(error)) {
        const delay = Math.min(
          opts.baseDelay * Math.pow(2, attempt) + Math.random() * 500,
          opts.maxDelay
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        break;
      }
    }
  }

  throw lastError;
}

// ============= SAFE EXECUTION =============

/**
 * Safely executes a function, returning null on error
 */
export async function safeExecute<T>(
  fn: () => Promise<T>,
  source: string,
  context?: Record<string, unknown>
): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    logError(source, error, context);
    return null;
  }
}

/**
 * Safely parses JSON, returning null on error
 */
export function safeJsonParse<T>(json: string, source: string): T | null {
  try {
    return JSON.parse(json) as T;
  } catch (error) {
    logError(source, error, { jsonPreview: json.slice(0, 100) });
    return null;
  }
}
